import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { uniqueId } from 'lodash';

import { useTranslation } from 'react-i18next';

// ui
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import NativeSelect from '@material-ui/core/NativeSelect';
import MaterialTable, { MTableToolbar } from 'material-table';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';

// components
import DamModal from '../../../../components/modal/Modal';
import DamChartDailyMediumMain from '../../chart/daily/DamChartDailyMediumMain';

// Utils
import formatNumber from '../../../../utils/number/Number';
import exportCsv from '../../../../utils/export/Csv';

// style
import { StyledIconButton } from '../../../../components/button/Button';
import styles from '../style';
import tableIcons from '../../../../components/table/MaterialTableIcon';
import DamMediumColor from '../../../../components/color/dam/DamMediumColor';
import damStatusColor from '../../../../components/color/dam/DamStatusColor';

const useStyles = makeStyles(styles);

export default function DamLatestMedium(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    datas, handleSearchMarker, currentMode, currentArea, userIsLogin,
  } = props;
  const isExport = (userIsLogin) ? { csv: true } : userIsLogin;

  const [selectSituationLevel, setSituationLevel] = useState('');
  const [damMediumData, setDamMediumData] = useState([]);
  const [modalData, setModalData] = useState({
    open: false,
    header: t('กราฟอ่างเก็บน้ำรายวัน กรมชลประทาน'),
    subheader: null,
    content: null,
  });

  useEffect(() => {
    setDamMediumData(datas);
  }, [datas]);

  function filterData(currentData, mode, area) {
    let filterD = [];
    if (mode === 'province' && area.code !== '0') {
      filterD = currentData.filter((d) => d.geocode.province_code === area.code);
    } else if (currentMode === 'basin' && area.code !== '0') {
      filterD = currentData.filter((d) => d.basin.basin_code === parseInt(area.code, 10));
    } else {
      filterD = currentData;
    }

    return filterD;
  }

  const handleChart = (data) => (e) => {
    e.preventDefault();

    setModalData({
      open: true,
      header: t('กราฟอ่างเก็บน้ำขนาดกลางรายวัน กรมชลประทาน'),
      subheader: null,
      content: <DamChartDailyMediumMain id={data} agencyId={12} />,
    });
  };

  const handleSituationChange = (event) => {
    const selectVar = event.target.value;
    const dataFilter = (selectVar === '') ? datas : datas.filter((d) => DamMediumColor(d.dam_storage_percent).color.statusColor === selectVar);

    setSituationLevel(selectVar);
    setDamMediumData(dataFilter);
  };

  return (
    <>
      <Paper className={classes.root}>
        <MaterialTable
          icons={tableIcons}
          columns={[
            {
              title: t('อ่างเก็บน้ำ'),
              title_export: t('อ่างเก็บน้ำ'),
              field: 'dam.dam_name.th',
              width: 230,
              headerStyle: {
                left: 5,
                position: 'sticky',
                zIndex: 50,
              },
              cellStyle: {
                left: 0,
                position: 'sticky',
                zIndex: 50,
                wordWrap: 'normal',
                padding: '0em',
              },
              render: (rowData) => {
                let ridguid = '';
                let nwccId = '';
                let agencyName = '';

                if (rowData.agency.agency_name.th) {
                  agencyName = rowData.agency.agency_name.th;
                }
                if (rowData.dam.rid_guid) {
                  ridguid = `\nรหัสกรมชลฯ ${rowData.dam.rid_guid}`;
                }
                if (rowData.dam.nwcc_id) {
                  nwccId = `\nรหัส สทนช ${rowData.dam.nwcc_id}`;
                }

                return (
                  <Box display="flex" style={{ backgroundColor: 'white' }}>
                    <Button
                      onClick={handleSearchMarker(
                        rowData.id + rowData.dam.id + rowData.geocode.id,
                        rowData.dam.dam_lat,
                        rowData.dam.dam_long,
                      )}
                      title={`${agencyName}${ridguid}${nwccId}`}
                      data-cy={rowData.dam.dam_name.th}
                    >
                      {`${rowData.dam.dam_name.th.replace('อ่างเก็บน้ำ', '')}`}
                    </Button>
                  </Box>
                );
              },
            },
            {
              title: t('รหัสสถานี'),
              type: 'string',
              field: 'dam.dam_oldcode',
              hidden: true,
              export: false,
              searchable: true,
            },
            {
              title: t('จังหวัด'),
              title_export: t('จังหวัด'),
              field: 'geocode.province_name.th',
              width: 140,
            },
            {
              title: t('ลุ่มน้ำ'),
              title_export: t('ลุ่มน้ำ'),
              field: 'basin.basin_name.th',
              width: 150,
              hidden: !userIsLogin,
            },
            {
              title:
  <>
    {t('ความจุ')}
    <br />
    <Typography variant="caption" display="block">ล้าน ลบ.ม</Typography>
  </>,
              field: 'dam.normal_storage',
              title_export: t('ความจุ (ล้าน ลบ.ม.)'),
              type: 'numeric',
              width: 100,
            },
            {
              title:
  <>
    {t('ปริมาณน้ำ')}
    <br />
    <Typography variant="caption" display="block">{t('ล้าน ลบ.ม (% ความจุ)')}</Typography>
  </>,
              field: 'dam_storage_percent',
              title_export: t('ปริมาณน้ำ (ล้าน ลบ.ม.)'),
              type: 'numeric',
              width: 110,
              render: (rowData) => {
                const status = DamMediumColor(rowData.dam_storage_percent);

                // no transaction data
                if (rowData.dam_date === '1970-01-01' || !rowData.dam_storage) {
                  return '-';
                }

                return (
                  <span>
                    {formatNumber(rowData.dam_storage)}
                    {' '}
                    <span style={{ color: status.color.statusColor }}>
                      (
                      {rowData.dam_storage_percent.toFixed(0)}
                      %)
                    </span>
                  </span>
                );
              },
            },
            {
              title:
  <>
    {t('ปริมาตรใช้การ')}
    <br />
    <Typography variant="caption" display="block">{t('ล้าน ลบ.ม (%น้ำใช้การได้)')}</Typography>
  </>,
              field: 'dam_uses_water',
              title_export: t('ปริมาตรใช้การ (ล้าน ลบ.ม.)'),
              type: 'numeric',
              width: 130,
              render: (rowData) => {
                // calculate percent dam_uses_water
                let percentDamUsesWater = '--';
                if (rowData.dam.min_storage
                    && rowData.dam.normal_storage
                    && rowData.dam_uses_water
                ) {
                  percentDamUsesWater = (
                    (rowData.dam_uses_water * 100) / (rowData.dam.normal_storage - rowData.dam.min_storage)
                  ).toFixed(0);
                }

                // no transaction data
                if (rowData.dam_date === '1970-01-01' || !rowData.dam_uses_water) {
                  return '-';
                }

                return (
                  <span>
                    {rowData.dam_uses_water.toFixed(2)}
                    {' ('}
                    {percentDamUsesWater}
                    {'%)'}
                  </span>
                );
              },
            },
            {
              title:
  <>
    {t('น้ำไหลลง')}
    <br />
    <Typography variant="caption" display="block">{t('ล้าน ลบ.ม')}</Typography>
  </>,
              field: 'dam_inflow',
              title_export: 'น้ำไหลลง (ล้าน ลบ.ม)',
              type: 'numeric',
              width: 120,
              render: (rowData) => {
                // no transaction data
                if (rowData.dam_date === '1970-01-01') {
                  return '-';
                }

                return rowData.dam_inflow;
              },
            },
            {
              title:
  <>
    {t('น้ำระบาย')}
    <br />
    <Typography variant="caption" display="block">{t('ล้าน ลบ.ม')}</Typography>
  </>,
              field: 'dam_released',
              title_export: t('น้ำระบาย (ล้าน ลบ.ม.)'),
              type: 'numeric',
              width: 120,
              render: (rowData) => {
                // no transaction data
                if (rowData.dam_date === '1970-01-01') {
                  return '-';
                }

                return rowData.dam_released;
              },
            },
            {
              title: '',
              field: '',
              sorting: false,
              width: 60,
              render: (rowData) => {
                // no transaction data
                if (rowData.dam_date === '1970-01-01') {
                  return '';
                }

                return (
                  <>
                    <Tooltip title={t('กราฟรายปี')}>
                      <StyledIconButton onClick={handleChart(rowData.dam.id)}>
                        <FontAwesomeIcon icon={faChartLine} style={{ color: '#888' }} />
                      </StyledIconButton>
                    </Tooltip>
                  </>
                );
              },
            },
          ]}
          data={filterData(damMediumData, currentMode, currentArea)}
          localization={{
            toolbar: {
              searchPlaceholder: t('ค้นหา'),
              exportTitle: t('ดาวน์โหลดข้อมูล แหล่งน้ำขนาดกลาง'),
            },
            pagination: {
              labelRowsSelect: t('แถว'),
            },
            body: { emptyDataSourceMessage: damMediumData.length === 0 && selectSituationLevel === '' ? <h3>{t('กำลังโหลดข้อมูล...')}</h3> : <h3>{t('ไม่มีข้อมูลแหล่งน้ำขนาดกลาง ตามเงื่อนไข')}</h3> },
          }}
          options={{
            id: 'latest-medium-dam',
            searchFieldStyle: {
              width: '50%',
              alignSelf: 'flex-end',
            },
            showTitle: false,
            // search: true,
            exportButton: isExport,
            exportCsv,
            cellStyle: {
              padding: 8,
            },
            toolbar: true,
            pageSize: 10,
            sorting: true,
          }}
          components={{
            Toolbar: (propsToolbar) => (
              <Grid container direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
                <Grid item xs={12} sm={8} md={6}>
                  <MTableToolbar {...propsToolbar} />
                </Grid>
                <Grid item xs="auto" sm="auto" md="auto">
                  <FormControl className={classes.formControl}>
                    <NativeSelect
                      id="status"
                      value={selectSituationLevel}
                      onChange={handleSituationChange}
                    >
                      <option value="">{t('แสดงทุกปริมาณน้ำ')}</option>
                      {damStatusColor.slice(1).map((val) => (
                        <option key={uniqueId()} value={val.statusColor}>
                          {`${val.label} (${val.rangeTitle}%)`}
                        </option>
                      ))}
                    </NativeSelect>
                  </FormControl>
                </Grid>
              </Grid>
            ),
          }}
        />

        <DamModal
          open={modalData.open}
          modalHeader={modalData.header}
          modalSubHeader={modalData.subheader}
          modalContent={modalData.content}
        />
      </Paper>
    </>
  );
}

DamLatestMedium.propTypes = {
  datas: PropTypes.instanceOf(Array).isRequired,
  handleSearchMarker: PropTypes.func.isRequired,
  currentMode: PropTypes.string.isRequired,
  currentArea: PropTypes.instanceOf(Object).isRequired,
  userIsLogin: PropTypes.bool.isRequired,
};
